import { Vector } from './Vector';

export interface ParticleProps {
  size: number;
  angle: number;
  spread: number;
  speed: number;
  fade: number;
  invert?: boolean;
  bounceX?: number;
  bounceY?: number;
}

export class Particle {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private props: ParticleProps;
  private radius = 0;

  public pos: Vector;
  public vel: Vector;
  public acc: Vector = new Vector();
  public size: number;
  public speed: number;
  public angle: number;
  public life = 1;

  constructor(
    canvas: HTMLCanvasElement,
    x: number,
    y: number,
    props: ParticleProps,
  ) {
    this.canvas = canvas;
    this.context = canvas.getContext('2d')!;
    this.props = props;

    this.size = Vector.randomRange(props.size / 2, props.size);
    this.angle = Vector.randomRange(
      props.angle - props.spread / 2,
      props.angle + props.spread / 2,
    );
    this.speed = Vector.randomRange(props.speed * 1.1, props.speed / 1.1);
    this.pos = new Vector(x, y);
    this.vel = Vector.fromAngle(this.angle, this.speed);
  }

  public setForce(force: Vector): void {
    this.acc = force;
  }

  public loop(): void {
    this.life = Math.max(0, this.life - this.props.fade / 1000);

    this.radius = this.size * this.computeRadiusRatio();

    this.vel.add(this.acc);

    const { width, height } = this.canvas;

    if (this.pos.x > width || this.pos.x <= 0) {
      this.vel.x *= this.props.bounceX ?? 0;
    }

    if (this.pos.y > height || this.pos.y <= 0) {
      this.vel.y *= this.props.bounceY ?? 0;
    }

    this.pos.add(this.vel);

    if (
      this.pos.x - this.size > width ||
      this.pos.x + this.size <= 0 ||
      this.pos.y - this.size > height
    ) {
      this.life = 0;
    }

    this.render();
  }

  private computeRadiusRatio(): number {
    const { invert } = this.props;
    return invert ? (1.05 - this.life) * 0.95 : this.life;
  }

  private render(): void {
    const { height } = this.canvas;
    if (this.pos.y - this.size > height || this.pos.y + this.size <= 0) {
      return;
    }

    this.context.beginPath();
    this.context.arc(
      Math.round(this.pos.x),
      Math.round(this.pos.y),
      Math.round(this.radius),
      0,
      2 * Math.PI,
    );
    this.context.fillStyle = 'rgba(255,255,255,1)';
    this.context.fill();
  }
}
